import { useState, type FormEvent } from "react";
import type { AlertRule, Comparison, MetricName, Severity } from "../api/types";
import { COMPARISONS, METRIC_NAMES, SEVERITIES } from "../api/types";
import {
  useAlertRules,
  useDeleteAlertRule,
  useSaveAlertRule,
} from "../hooks/queries";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/rbac";
import { metricLabel } from "../hooks/metrics";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { Input, Select } from "../components/ui/Input";
import { TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { errorMessage } from "../api/errors";

interface RuleForm {
  id?: string;
  name: string;
  metric: MetricName;
  comparison: Comparison;
  threshold: number;
  durationSeconds: number;
  severity: Severity;
  channels: string;
  enabled: boolean;
}

const emptyForm: RuleForm = {
  name: "",
  metric: "cpu.usage_percent",
  comparison: "gt",
  threshold: 90,
  durationSeconds: 600,
  severity: "high",
  channels: "email",
  enabled: true,
};

const comparisonLabels: Record<Comparison, string> = {
  gt: "> (büyük)",
  gte: "≥ (büyük eşit)",
  lt: "< (küçük)",
  lte: "≤ (küçük eşit)",
};

function toForm(rule: AlertRule): RuleForm {
  return {
    id: rule.id,
    name: rule.name,
    metric: rule.metric,
    comparison: rule.comparison,
    threshold: rule.threshold,
    durationSeconds: rule.durationSeconds,
    severity: rule.severity,
    channels: (rule.channels ?? []).join(", "),
    enabled: rule.enabled,
  };
}

export function AlertRulesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const rulesQuery = useAlertRules();
  const saveMutation = useSaveAlertRule();
  const deleteMutation = useDeleteAlertRule();

  const [form, setForm] = useState<RuleForm | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AlertRule | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const canEdit = can(user?.role, "manageAlertRules");

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    if (!form.name.trim()) {
      setFormError("Kural adı zorunludur.");
      return;
    }
    setFormError(null);
    const rule: AlertRule = {
      id: form.id ?? "",
      name: form.name.trim(),
      metric: form.metric,
      comparison: form.comparison,
      threshold: Number(form.threshold),
      durationSeconds: Number(form.durationSeconds),
      severity: form.severity,
      channels: form.channels
        .split(",")
        .map((channel) => channel.trim())
        .filter(Boolean),
      enabled: form.enabled,
    };
    saveMutation.mutate(
      { rule, id: form.id },
      {
        onSuccess: () => {
          toast(form.id ? "Kural güncellendi." : "Kural oluşturuldu.", "success");
          setForm(null);
        },
        onError: (error) => setFormError(errorMessage(error)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Uyarı Kuralları</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Eşik tabanlı kurallar: metrik + koşul + süre + önem + kanal.
          </p>
        </div>
        <Button size="sm" disabled={!canEdit} onClick={() => setForm({ ...emptyForm })}>
          Yeni kural
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tanımlı kurallar</CardTitle>
          <CardDescription>Alert motoru bu kuralları değerlendirir</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {rulesQuery.isPending ? (
            <TableSkeleton rows={3} columns={6} />
          ) : rulesQuery.isError ? (
            <ErrorState
              message="Kurallar yüklenemedi."
              onRetry={() => void rulesQuery.refetch()}
            />
          ) : (rulesQuery.data?.length ?? 0) === 0 ? (
            <EmptyState
              title="Kural yok"
              description="Henüz tanımlı uyarı kuralı bulunmuyor."
              actionLabel={canEdit ? "Yeni kural oluştur" : undefined}
              onAction={canEdit ? () => setForm({ ...emptyForm }) : undefined}
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Ad</TH>
                  <TH>Metrik</TH>
                  <TH>Koşul</TH>
                  <TH>Süre</TH>
                  <TH>Önem</TH>
                  <TH>Kanallar</TH>
                  <TH>Durum</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {rulesQuery.data?.map((rule) => (
                  <TR key={rule.id}>
                    <TD className="font-medium">{rule.name}</TD>
                    <TD className="text-muted-foreground text-xs">
                      {metricLabel(rule.metric)}
                    </TD>
                    <TD className="text-sm">
                      {comparisonLabels[rule.comparison]} {rule.threshold}
                    </TD>
                    <TD className="text-muted-foreground text-sm">
                      {Math.round(rule.durationSeconds / 60)} dk
                    </TD>
                    <TD>
                      <Badge
                        tone={
                          rule.severity === "critical"
                            ? "danger"
                            : rule.severity === "high"
                              ? "warning"
                              : rule.severity === "medium"
                                ? "info"
                                : "muted"
                        }
                      >
                        {rule.severity}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        {(rule.channels ?? []).map((channel) => (
                          <Badge key={channel} tone="muted">
                            {channel}
                          </Badge>
                        ))}
                      </div>
                    </TD>
                    <TD>
                      {rule.enabled ? (
                        <Badge tone="success">Etkin</Badge>
                      ) : (
                        <Badge tone="muted">Kapalı</Badge>
                      )}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canEdit}
                          onClick={() => setForm(toForm(rule))}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canEdit}
                          onClick={() => setPendingDelete(rule)}
                        >
                          Sil
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Oluştur / düzenle */}
      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        title={form?.id ? "Kuralı düzenle" : "Yeni uyarı kuralı"}
        description="Koşul sağlandığında belirtilen süre boyunca devam ederse uyarı tetiklenir."
      >
        {form ? (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Input
              label="Kural adı"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Metrik"
                options={METRIC_NAMES.map((metric) => ({
                  value: metric,
                  label: metricLabel(metric),
                }))}
                value={form.metric}
                onChange={(event) =>
                  setForm({ ...form, metric: event.target.value as MetricName })
                }
              />
              <Select
                label="Koşul"
                options={COMPARISONS.map((comparison) => ({
                  value: comparison,
                  label: comparisonLabels[comparison],
                }))}
                value={form.comparison}
                onChange={(event) =>
                  setForm({ ...form, comparison: event.target.value as Comparison })
                }
              />
              <Input
                label="Eşik değeri"
                type="number"
                step="0.1"
                required
                value={String(form.threshold)}
                onChange={(event) =>
                  setForm({ ...form, threshold: Number(event.target.value) })
                }
              />
              <Input
                label="Süre (saniye)"
                type="number"
                min={0}
                required
                value={String(form.durationSeconds)}
                onChange={(event) =>
                  setForm({ ...form, durationSeconds: Number(event.target.value) })
                }
              />
              <Select
                label="Önem"
                options={SEVERITIES.map((severity) => ({
                  value: severity,
                  label: severity,
                }))}
                value={form.severity}
                onChange={(event) =>
                  setForm({ ...form, severity: event.target.value as Severity })
                }
              />
              <Input
                label="Kanallar"
                hint="Virgülle ayırın: email, slack-ops, webhook-ops"
                value={form.channels}
                onChange={(event) => setForm({ ...form, channels: event.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
                className="accent-primary h-4 w-4"
              />
              Kural etkin
            </label>
            {formError ? (
              <p role="alert" className="bg-danger/10 text-danger rounded-md border border-danger/30 px-3 py-2 text-sm">
                {formError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>
                Vazgeç
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
              </Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      {/* Silme onayı */}
      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Kural silinsin mi?"
        description="Bu işlem geri alınamaz. Kurala ait mevcut uyarılar korunur."
      >
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (!pendingDelete) return;
              deleteMutation.mutate(pendingDelete.id, {
                onSuccess: () => {
                  toast("Kural silindi.", "success");
                  setPendingDelete(null);
                },
                onError: (error) => toast(errorMessage(error), "error"),
              });
            }}
          >
            Sil
          </Button>
        </div>
      </Dialog>
    </div>
  );
}